import { DEBOUNCE_TIME } from './src-constants-api.constants.js';
import { ensureUserTestId } from '@vp/ab-test-cookie';
import { l as lodashExports } from './_virtual-lodash.js';
import { devtools } from './node_modules-zustand-esm-middleware.js';
import { fetchCouponDetails } from './src-clients-couponCallout.client.js';
import { PageId } from './src-constants-pageId.js';
import { getQueryKey } from './src-utils-generic.util.js';
import { create } from './node_modules-zustand-esm-react.js';

const debounceFunctionCache = {};
const getEmptyScopedData = () => ({
  couponDetails: {},
  queryMap: {},
  transitionedQueryMap: {}
});
const setScopedDebouncedCouponDetails = (baseConfig, getStore, setStore) => {
  const scope = baseConfig.pageId;
  if (!debounceFunctionCache[scope]) {
    debounceFunctionCache[scope] = lodashExports.debounce(
      async (getStore2, setStore2) => {
        const { queryMap } = getStore2().data[scope];
        const { pricingContext, couponCode } = getStore2();
        const { locale, tenant, pageId } = baseConfig;
        const queries = Object.values(queryMap);
        if (!tenant || !locale || queries.length === 0) return;
        try {
          setScopedData(getStore2, setStore2, scope, "transitionedQueryMap", { ...queryMap });
          setScopedData(getStore2, setStore2, scope, "queryMap", {});
          const testUserId = ensureUserTestId();
          const request = {
            tenant,
            locale,
            pageId,
            requestor: "paa-coupon-component-" + pageId,
            couponCode,
            testUserId,
            productData: queries,
            pricingContext: pricingContext?.value
          };
          const couponDetails = await fetchCouponDetails(request);
          setScopedData(
            getStore2,
            setStore2,
            scope,
            "couponDetails",
            {
              productCouponData: {
                ...getStore2().data[scope].couponDetails.productCouponData,
                ...couponDetails.productCouponData
              },
              ...Object.keys(couponDetails).reduce((acc, key) => {
                if (key !== "productCouponData") {
                  acc[key] = couponDetails[key] ?? getStore2().data[scope].couponDetails[key];
                }
                return acc;
              }, {})
            }
          );
          setScopedData(getStore2, setStore2, scope, "transitionedQueryMap", {});
        } catch (error) {
          setScopedData(getStore2, setStore2, scope, "transitionedQueryMap", {});
          console.error("Error fetching coupon details for queries:", queries, error);
        }
      },
      /* DEBOUNCE_PAGEID_MAPPING[]  || */
      DEBOUNCE_TIME
    );
  }
  return debounceFunctionCache[scope](getStore, setStore);
};
const useCouponDetailsStore = create()(
  devtools(
    (set, get) => ({
      data: {
        [PageId.UnknownPage]: getEmptyScopedData()
      },
      setCouponCode: (couponCode) => {
        set({ couponCode });
      },
      setVatIncl: (vatIncl) => {
        set({ vatIncl });
      },
      setLocalizations: (localizations) => {
        set({ localizations });
      },
      addQuery: async (query, baseConfig) => {
        const scope = baseConfig.pageId;
        if (!scope) return;
        if (!(query.mpvId || query.productId && query.version)) {
          return;
        }
        const queryId = getQueryKey(query);
        if (get().data[scope].queryMap[queryId] || get().data[scope].couponDetails?.productCouponData?.[queryId] || get().data[scope].transitionedQueryMap[queryId]) {
          return;
        }
        query.productVariantId = queryId;
        const existingQuery = get().data[scope].queryMap;
        setScopedData(get, set, scope, "queryMap", { ...existingQuery, [queryId]: query });
        setScopedDebouncedCouponDetails(baseConfig, get, set);
      },
      setPricingContext: (pricingContext) => {
        set({ pricingContext: { value: pricingContext, isInitialized: true } });
      },
      initializeScopedData: (scope) => {
        const existingData = get().data;
        if (!existingData[scope]) {
          set({ data: { ...existingData, [scope]: getEmptyScopedData() } });
        }
      }
    })
  )
);
const setScopedData = (get, set, scope, fieldName, value) => {
  const existingData = get().data;
  set({ data: { ...existingData, [scope]: { ...existingData[scope], [fieldName]: value } } });
};

export { useCouponDetailsStore };
//# sourceMappingURL=src-store-couponDetails.store.js.map
